import { camelCase } from 'lodash';

import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import Workflow from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';

/**
 * The one place the referral experience asks "may this user do that" (OLIO-2615).
 *
 * These gates are UX only. The API enforces the same rules and 403s regardless, so a component
 * hiding a button is saving someone a pointless click, never protecting anything.
 */
export default function useWorkflowPermissions() {
  const { profile } = useProfile();

  const canView = profile.has(PERMISSIONS.workflowInboundReferralView);
  const canCreate = profile.has(PERMISSIONS.workflowInboundReferralCreate);
  const canEdit = profile.has(PERMISSIONS.workflowInboundReferralEdit);

  /**
   * Whether the user may act on one action right now: complete, save progress, re-run, amend.
   *
   * Two questions, deliberately answered together. WHO: workflow-level edit is a superset, so an
   * editor can act on every kind; a kind's own grant exists for the narrow specialist who holds
   * nothing else, and a kind with no grant of its own (`editPermissionName: null`) rides workflow
   * edit alone. WHETHER: a referral whose patient has been admitted accepts no writes at all.
   *
   * The lock is folded in here rather than left to each call site because every caller is a write
   * the API refuses once the referral is locked — a caller that forgot would render a control that
   * 422s. `workflow` is a required argument for the same reason: the compiler, not a reviewer, is
   * what keeps the next action card from reintroducing the gap.
   *
   * NOT `workflow.isTerminal`, which is genuinely per-surface: amending a completed referral is
   * the designed correction path (the API reopens the run for it), while most kinds refuse a
   * completion on a closed run. Each caller keeps that decision.
   *
   * The name arrives snake_case while `profile.permissions` is camelized, hence the conversion.
   * lodash camelCase would mangle an acronym (`ai` -> `Ai` where the payload says `AI`), which no
   * kind name contains today; a kind that introduces one needs a real mapping instead.
   */
  const canEditAction = (action: WorkflowAction, workflow: Workflow) => {
    if (workflow.isLocked) return false;
    if (canEdit) return true;
    if (!action.editPermissionName) return false;

    // Cast because `permissions` is typed to the PERMISSIONS keys, and the per-kind names are
    // deliberately absent from that map — the API owns which kind needs which grant.
    const held = profile.permissions as Record<string, boolean | undefined> | undefined;

    return !!held?.[camelCase(action.editPermissionName)];
  };

  return { canView, canCreate, canEdit, canEditAction };
}
